import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { knowledgeTreeStructure } from '../../mocks/knowledgeTreeData';
import { knowledgeCategories } from '../../mocks/knowledgeData';

export default function KnowledgeDetail() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const categoryData = categoryId ? knowledgeTreeStructure[categoryId as keyof typeof knowledgeTreeStructure] : null;
  const categoryInfo = knowledgeCategories.find(cat => cat.id === categoryId);

  useEffect(() => {
    if (categoryData && categoryData.branches.length > 0) {
      setSelectedBranch(categoryData.branches[0].id);
    }
  }, [categoryData]);

  if (!categoryData || !categoryInfo) {
    return <div>知识板块不存在</div>;
  }

  const currentBranch = categoryData.branches.find(b => b.id === selectedBranch);

  const toggleNode = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar />
      
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-6">
              <div 
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: `${categoryInfo.color}15` }}
              >
                <i className={`${categoryInfo.icon} text-4xl`} style={{ color: categoryInfo.color }}></i>
              </div>
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-4">{categoryData.name}</h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">{categoryData.description}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-24">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <i className="ri-git-branch-line" style={{ color: categoryInfo.color }}></i>
                  知识分支
                </h3>
                <div className="space-y-2">
                  {categoryData.branches.map((branch) => (
                    <button
                      key={branch.id}
                      onClick={() => setSelectedBranch(branch.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                        selectedBranch === branch.id
                          ? 'bg-gradient-to-r text-white font-medium shadow-md'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      }`}
                      style={selectedBranch === branch.id ? {
                        backgroundImage: `linear-gradient(135deg, ${categoryInfo.color}, ${categoryInfo.color}dd)`
                      } : {}}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm">{branch.name}</span>
                        <span className={`text-xs ${selectedBranch === branch.id ? 'text-white/80' : 'text-gray-500'}`}>
                          {branch.nodes.reduce((sum, node) => sum + node.articles, 0)}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              {currentBranch && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">{currentBranch.name}</h2>
                    <p className="text-gray-600">
                      共 {currentBranch.nodes.reduce((sum, node) => sum + node.articles, 0)} 篇文章
                    </p>
                  </div>

                  <div className="space-y-6">
                    {currentBranch.nodes.map((node) => (
                      <div key={node.id} className="border border-gray-200 rounded-xl overflow-hidden">
                        <div
                          onClick={() => toggleNode(node.id)}
                          className="flex items-center justify-between p-5 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: `${categoryInfo.color}15` }}
                            >
                              <i className="ri-folder-line text-lg" style={{ color: categoryInfo.color }}></i>
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{node.name}</h3>
                              <p className="text-sm text-gray-500">{node.articles} 篇文章</p>
                            </div>
                          </div>
                          <i 
                            className={`ri-arrow-${expandedNodes.has(node.id) ? 'up' : 'down'}-s-line text-xl text-gray-400 transition-transform`}
                          ></i>
                        </div>

                        {expandedNodes.has(node.id) && (
                          <div className="p-5 bg-white border-t border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              {node.subNodes.map((subNode, index) => (
                                <a
                                  key={index}
                                  href={`/article/${node.id}/${index}`}
                                  className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer group"
                                >
                                  <div 
                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: categoryInfo.color }}
                                  ></div>
                                  <span className="text-sm text-gray-700 group-hover:text-gray-900 flex-1">
                                    {subNode}
                                  </span>
                                  <i 
                                    className="ri-arrow-right-line text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                                    style={{ color: categoryInfo.color }}
                                  ></i>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 p-6 bg-gradient-to-br rounded-xl text-white" style={{
                    backgroundImage: `linear-gradient(135deg, ${categoryInfo.color}, ${categoryInfo.color}dd)`
                  }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-bold mb-2">探索更多知识</h3>
                        <p className="text-white/80 text-sm">
                          继续深入了解 {currentBranch.name} 相关内容
                        </p>
                      </div>
                      <button className="px-6 py-3 bg-white text-gray-900 rounded-lg font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer">
                        查看全部
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">相关知识板块</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {knowledgeCategories
                .filter(cat => cat.id !== categoryId)
                .slice(0, 4)
                .map((cat) => (
                  <a
                    key={cat.id}
                    href={`/knowledge/${cat.id}`}
                    className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all border border-gray-100 cursor-pointer group"
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${cat.color}15` }}
                    >
                      <i className={`${cat.icon} text-2xl`} style={{ color: cat.color }}></i>
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2 group-hover:text-gray-700">{cat.name}</h4>
                    <p className="text-sm text-gray-600">{cat.description}</p>
                  </a>
                ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}